import { KeyboardEvent } from "react";
import { Card } from "react-bootstrap";
import { Lists } from "../../../lists";
import { Claims, Surgery } from "../../../../models";
import { Icon, far } from "../../../../component-library";
import { Link, useNavigate } from "react-router-dom";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { SurgeryThumbnail } from "../../../surgeries";
import { usePermissions } from "../../../../contexts";
import { getSurgeryTypeColorClass } from "../../../../helper-functions";

interface UnfilledSurgeriesProps {
    surgeries: Surgery[];
}

function DraggableSurgery({ surgery, index }: Readonly<{ surgery: Surgery; index: number }>) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canCreateOffer = hasPermission(Claims.OfferTissue);
    const baseUrl = `/lists/${Lists.SurgeriesUnfilled}/surgeries`;

    const { attributes, listeners, setNodeRef, transform } = useDraggable({
        id: `surgery-${surgery.id}-${index}`,
        disabled: !canCreateOffer,
    });

    const style = transform
        ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          }
        : undefined;

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            navigate(`${baseUrl}/${surgery.id}`);
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...listeners}
            {...attributes}
            className={`surgery-list-item draggable-surgery ${getSurgeryTypeColorClass(surgery)}`}
            tabIndex={0}
            onClick={() => navigate(`${baseUrl}/${surgery.id}`)}
            onKeyDown={handleKeyDown}
        >
            <SurgeryThumbnail surgery={surgery} surgeon={surgery.surgeon} />
        </div>
    );
}

export function UnfilledSurgeries({ surgeries }: Readonly<UnfilledSurgeriesProps>) {
    const { hasPermission } = usePermissions();
    const baseUrl = `/lists/${Lists.SurgeriesUnfilled}/surgeries`;

    const { setNodeRef } = useDroppable({
        id: "surgeries",
    });

    if (!hasPermission(Claims.ViewSurgery)) {
        return null;
    }

    return (
        <div className="unfilled-surgeries-container pe-3 pb-3">
            <Card className="bg-primary text-white unfilled-surgeries">
                <Card.Header className="d-flex justify-content-between">
                    <Link to={baseUrl} className="text-white">
                        Unfilled Surgeries
                    </Link>
                    <Link
                        to={"/surgery-calendar"}
                        className="text-white"
                        title="View Surgery Calendar"
                        aria-label="View Surgery Calendar"
                    >
                        <Icon icon={far.faCalendar} />
                    </Link>
                </Card.Header>
                <Card.Body className="bg-canvas text-primary unfilled-surgeries-list">
                    {surgeries.length ? (
                        <div ref={setNodeRef}>
                            {surgeries.map((surgery, index) => (
                                <DraggableSurgery key={`${surgery.id}-${index}`} surgery={surgery} index={index} />
                            ))}
                        </div>
                    ) : (
                        <div>No unfilled surgeries found.</div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}
